import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Play, Trophy, RotateCcw, UserX, Trash2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import TournamentIOToolbar from "@/components/director/TournamentIOToolbar";

import { BACKEND_URL } from '../utils/apiBase';
const API = `${BACKEND_URL}/api`;

const CircleManager = ({ tournament, athletes, judges, onReload, startCircle: centralStartCircle }) => {
  const { t } = useI18n();
  const [circles, setCircles] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [showCsvErrors, setShowCsvErrors] = useState(false);

  const loadCircles = useCallback(async () => {
    if (!tournament?.id) return;
    try {
      const res = await axios.get(`${API}/tournament/${tournament.id}/circles`);
      setCircles(res.data);
    } catch (error) {
      console.error("Error loading circles:", error);
    }
  }, [tournament?.id]);

  useEffect(() => {
    loadCircles();
    const interval = setInterval(loadCircles, 1000);
    return () => clearInterval(interval);
  }, [loadCircles]);

  const assignAthlete = async (circleId, athleteId, position) => {
    try {
      await axios.put(`${API}/circle/${circleId}/assign`, { athlete_id: athleteId, position });
      toast.success(t('circle.assigned'));
      loadCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const unassignAthlete = async (circleId, position) => {
    try {
      await axios.put(`${API}/circle/${circleId}/unassign/${position}`);
      toast.success(t('circle.removed'));
      loadCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const startCircle = async (circleId) => {
    if (centralStartCircle) {
      const ok = await centralStartCircle(circleId);
      if (ok) loadCircles();
      return;
    }
    try {
      await axios.post(`${API}/circle/${circleId}/start`);
      toast.success(t('circle.started'));
      loadCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const forfeitAthlete = async (circleId, athleteId) => {
    if (!window.confirm(t('circle.forfeitConfirm'))) return;
    try {
      await axios.post(`${API}/circle/${circleId}/forfeit`, { athlete_id: athleteId });
      toast.success(t('circle.forfeited'));
      loadCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const cancelForfeit = async (circleId, athleteId) => {
    try {
      await axios.post(`${API}/circle/${circleId}/cancel-forfeit`, { athlete_id: athleteId });
      toast.success(t('circle.forfeitCancelled'));
      loadCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const resetCircle = async (circleId) => {
    if (!window.confirm(t('circle.resetConfirm'))) return;
    try {
      await axios.post(`${API}/circle/${circleId}/reset`);
      toast.success(t('circle.resetDone'));
      loadCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const getAssignedAthleteIds = () => {
    const ids = new Set();
    circles.forEach(c => c.athlete_ids.forEach(id => { if (id) ids.add(id); }));
    return ids;
  };

  const getAthleteById = (id) => athletes.find(a => a.id === id);

  return (
    <div data-testid="circle-manager">
      {/* Homogeneous Template / Import / Export toolbar */}
      <div className="mb-4">
        <TournamentIOToolbar
          templateUrl={`${API}/tournament/${tournament.id}/circles/template`}
          importUrl={`${API}/tournament/${tournament.id}/circles/assign-csv`}
          exportUrl={`${API}/tournament/${tournament.id}/circles/export`}
          testIdPrefix="circle-io"
          onImported={(data) => {
            const errs = data?.errors || [];
            if (errs.length > 0) {
              setCsvErrors(errs);
              setShowCsvErrors(true);
            } else {
              setCsvErrors([]);
            }
            loadCircles();
          }}
        />
      </div>

      {/* CSV Errors */}
      {showCsvErrors && csvErrors.length > 0 && (
        <Card className="p-4 mb-4 border border-red-500/50 bg-red-500/10">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-lg font-bold text-red-400">Import Errors</h4>
            <Button variant="ghost" size="sm" onClick={() => setShowCsvErrors(false)} className="text-gray-400">Close</Button>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {csvErrors.map((err, i) => (
              <div key={i} className="text-sm text-red-300">
                {err.row > 0 && `Row ${err.row}: `}{err.fid && `FID ${err.fid} - `}{err.error}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Circle Cards */}
      <div className="space-y-4">
        {circles.map(circle => {
          const forfeited = circle.forfeited_ids || [];
          return (
            <Card
              key={circle.id}
              className={`p-5 glass ${circle.status === 'completed' ? 'border border-green-500/30' : ''}`}
              data-testid={`circle-card-${circle.circle_number}`}
            >
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-foreground">Circle {circle.circle_number}</span>
                  <span className="text-sm px-2 py-1 rounded bg-purple-500/20 text-purple-700 dark:text-purple-300">
                    {circle.size} athletes
                  </span>
                </div>
                <span className={`text-lg px-3 py-1 rounded font-semibold ${
                  circle.status === 'completed' ? 'bg-green-500/20 text-green-700 dark:text-green-400' :
                  circle.status === 'voting' || circle.status === 'revealing' ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400' :
                  'bg-muted text-muted-foreground'
                }`}>
                  {circle.status}
                </span>
              </div>

              {/* Athlete assignments */}
              <div className={`grid gap-3 mb-3 ${circle.size === 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3'}`}>
                {circle.athlete_ids.map((aid, pos) => {
                  const athlete = aid ? getAthleteById(aid) : null;
                  const assignedIds = getAssignedAthleteIds();
                  const isForfeited = aid && forfeited.includes(aid);
                  
                  return (
                    <div key={pos} className="relative">
                      <div className="text-xs text-muted-foreground mb-1">Position {pos + 1}</div>
                      {circle.status === 'pending' ? (
                        <div>
                          <select
                            value={aid || ""}
                            onChange={(e) => {
                              if (e.target.value) assignAthlete(circle.id, e.target.value, pos);
                              else if (aid) unassignAthlete(circle.id, pos);
                            }}
                            className={`w-full p-2 rounded text-sm border ${
                              isForfeited 
                                ? 'bg-muted text-muted-foreground border-red-500/30 line-through' 
                                : 'bg-background text-foreground border-border'
                            }`}
                            disabled={isForfeited}
                            data-testid={`circle-${circle.circle_number}-pos-${pos + 1}`}
                          >
                            <option value="">Select athlete...</option>
                            {athletes.map(a => (
                              <option key={a.id} value={a.id} disabled={assignedIds.has(a.id) && a.id !== aid}>
                                {a.fid ? `[${a.fid}] ` : ''}{a.name} ({a.country})
                                {a.world_ranking ? ` #${a.world_ranking}` : ''}
                              </option>
                            ))}
                          </select>
                          {isForfeited && (
                            <div className="text-xs text-red-500 dark:text-red-400 mt-1 font-bold uppercase">{t('timer.forfeit')}</div>
                          )}
                        </div>
                      ) : (
                        <div className={`p-2 rounded text-center ${
                          isForfeited ? 'bg-muted/60 border border-red-500/20' :
                          circle.winner_id === aid ? 'bg-green-500/20 border border-green-500/50' :
                          'bg-muted'
                        }`} style={isForfeited ? { opacity: 0.5 } : {}}>
                          {athlete ? (
                            <>
                              <div className={`font-bold text-sm ${isForfeited ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{athlete.name}</div>
                              <div className="text-xs text-muted-foreground">{athlete.country}</div>
                              {isForfeited ? (
                                <div className="text-xs text-red-500 dark:text-red-400 font-bold mt-1 uppercase">{t('timer.forfeit')}</div>
                              ) : (
                                <>
                                  {circle.scores_summary && circle.scores_summary[aid] !== undefined && (
                                    <div className="text-lg font-bold text-purple-600 dark:text-purple-400 mt-1">{circle.scores_summary[aid]} pts</div>
                                  )}
                                  {circle.winner_id === aid && (
                                    <div className="text-xs text-green-600 dark:text-green-400 font-bold mt-1 flex items-center justify-center gap-1">
                                      <Trophy size={12} /> Winner
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          ) : (
                            <span className="text-muted-foreground italic text-sm">Empty</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Circle actions */}
              <div className="flex gap-2 flex-wrap">
                {circle.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => startCircle(circle.id)}
                      disabled={circle.athlete_ids.some(id => !id)}
                      className="bg-green-600 hover:bg-green-700"
                      size="sm"
                      data-testid={`start-circle-${circle.circle_number}`}
                    >
                      <Play size={14} className="mr-1" /> Start
                    </Button>
                    {circle.athlete_ids.filter(id => id && !forfeited.includes(id)).map(aid => {
                      const a = getAthleteById(aid);
                      return a ? (
                        <Button key={aid} onClick={() => forfeitAthlete(circle.id, aid)} variant="ghost" size="sm" className="text-red-400 text-xs">
                          <UserX size={12} className="mr-1" /> Forfait {a.name}
                        </Button>
                      ) : null;
                    })}
                    {forfeited.map(fid => {
                      const a = getAthleteById(fid);
                      return a ? (
                        <Button key={fid} onClick={() => cancelForfeit(circle.id, fid)} variant="ghost" size="sm" className="text-green-400 text-xs">
                          <RotateCcw size={12} className="mr-1" /> Cancel forfait {a.name}
                        </Button>
                      ) : null;
                    })}
                  </>
                )}
                {circle.status === 'completed' && (
                  <Button onClick={() => resetCircle(circle.id)} variant="ghost" size="sm" className="text-gray-400">
                    <RotateCcw size={14} className="mr-1" /> Reset
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Summary stats */}
      {circles.length > 0 && (
        <Card className="p-4 mt-4 glass">
          <div className="flex gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Total circles: </span>
              <span className="text-foreground font-bold">{circles.length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Completed: </span>
              <span className="text-green-600 dark:text-green-400 font-bold">{circles.filter(c => c.status === 'completed').length}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Pending: </span>
              <span className="text-foreground font-bold">{circles.filter(c => c.status === 'pending').length}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default CircleManager;
